import { FormikHelpers } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { Header } from "@/components/Header";
import { clearNewSuggestedDataFlag, IConsultantProfileDto } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { useCreateConsultantProfileFormAction } from "@/views/components/ConsultantProfile/hooks";
import { useOnboardingActions } from "@/views/Onboarding/hooks";
import { router } from "@/views/router";

import {
  IProfileSuggestedDataFormValues,
  ProfileSuggestedDataFormik,
  PROFILE_SUGGESTED_DATA_MESSENGER_CONTEXT,
} from "../../SuggestedData";
import completeProfileSvg from "./assets/complete-profile.svg";

import "./_complete-private-profile.scss";

interface ICompletePrivateProfileProps {
  profile: IConsultantProfileDto;
  isCvUploaded?: boolean;
  onCancel: () => void;
  onValuesChange: (values: IProfileSuggestedDataFormValues) => void;
  onProfileCreated?: () => void;
}

export const CompletePrivateProfile: FC<ICompletePrivateProfileProps> = props => {
  const history = useHistory();

  const { t } = useTranslation("session");

  const { defaultConsultantData, finishOnboarding, redirectJobRequestId } = useOnboardingActions();
  const createProfile = useCreateConsultantProfileFormAction(PROFILE_SUGGESTED_DATA_MESSENGER_CONTEXT);

  const handleSubmit = async (
    values: IProfileSuggestedDataFormValues,
    actions: FormikHelpers<IProfileSuggestedDataFormValues>,
  ) => {
    const profile = await createProfile({ ...defaultConsultantData, ...values }, actions);

    await clearNewSuggestedDataFlag({ consultantProfileId: profile.id });

    await finishOnboarding(profile.id, () => {
      history.push(router.get("dashboard").path);

      if (props.onProfileCreated) {
        props.onProfileCreated();
      }

      if (redirectJobRequestId) {
        history.push(router.get("job-requests.detail", { jobRequestId: redirectJobRequestId }).path);
      }
    });
  };

  const baseClass = "complete-private-profile";

  return (
    <Flex className={baseClass} direction="column" justify="center" align="center" innerMargin="xl">
      <Flex>
        <img className={`${baseClass}__svg`} src={completeProfileSvg} />
      </Flex>

      <Header size="lg" weight="bold" color="black" align="center">
        {t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.COMPLETE_PROFILE.HEADER")}
      </Header>

      <ProfileSuggestedDataFormik
        onCancel={props.onCancel}
        onSubmit={handleSubmit}
        profile={props.profile}
        onValuesChange={props.onValuesChange}
        showLocation
        skillsRequired
        initialValues={{
          ...props.profile,
          roles: props.profile.roles || [],
          skills: props.profile.skills || [],
          languages: props.profile.languages || [],
          location: props.profile.location || null,
        }}
      />
    </Flex>
  );
};
